// Media pass plumbing for the relay's key-addressed image routes (CYBORG-897).
//
// The relay is putting a credential on GET /api/assets/thumb and
// GET /api/assets/fs/:key. A browser `<img src>` cannot send an Authorization
// header, so those routes also accept a short-lived, per-USER pass in `?t=`.
// This module is the ONE place that pass is obtained and cached.
//
// WHY IT DEDUPES. The pass is per-user, not per-asset, so a page with 60 avatars
// needs exactly ONE mint. Without a shared in-flight promise, the moment a pass
// expires every one of those 60 images asks at the same instant and the relay
// takes 60 POSTs for 60 identical answers.
//
// THE FLAG IS ON IN PRODUCTION. Verified 2026-08-29: a credential-less
// /api/assets/thumb answers 401 `{"error":"unauthorized"}` and /api/assets/o/<key>
// 302s to the SPA. This module was written while it was off, and this paragraph used
// to say a passless URL "loads exactly as it does today" — it does not, so do not
// reason from that sentence.
//
// A missing or failed pass still does not THROW here; the URL is just built without
// `?t=` and goes out doomed. That buys "the caller decides", not "it works anyway",
// so every consumer must RECOVER when the pass lands instead of treating the first
// failure as final (Avatar re-arms on the requested url; BlurHashImage retries once
// through ensureMediaToken). `assetsRequireAuth()` still DEFAULTS off for self-host —
// the server tests pin that default; production sets the variable.
import { type FailureKind, reportUiFailure } from "$lib/report-failure.js";
import { coldStartTrace, errorSlug } from "$lib/desktop-cold-start-trace.js";
import { client } from "$lib/state/client.js";
import { CLOUD_RELAY_HTTP_BASE } from "$lib/core/relay-config.js";
import {
  assetKeyFromUrl,
  assetObjectKey,
  assetObjectUrl,
  toAssetObjectUrl,
} from "$lib/media/asset-object-url.js";
import { avatarImageUrl, avatarThumbUrl, presetAvatarThumbUrl } from "$lib/utils.js";

// Refresh this far before the pass actually dies, so a long-lived tab never
// paints with one that expires mid-flight.
const REFRESH_SKEW_MS = 5 * 60_000;

// A failed mint used to be TERMINAL. Nothing here retried, and `token` kept its old
// value on failure — so no `$state` changed, no `$derived` src recomputed, nothing
// re-rendered, and therefore nothing ever called back in to try again. The tab sat
// on grey boxes until a manual reload.
//
// That was survivable while the pass lasted 24h: a mint happened about once a day,
// and if it missed, the pass in hand was still good for hours. CYBORG-929 cut the
// TTL to 30 minutes (the pass IS the revocation window for this class, so short is
// correct and stays), which turned one mint a day into one every ~25 minutes — and
// every one of them has to land against the relay. A relay restart is ~30s of
// refusal; six deploys in a working day is six chances to catch a client mid-mint.
// The TTL change was right and the recovery path simply had not been sized for it.
//
// Full jitter, same shape as the WS client's reconnect: N tabs that all failed
// against the same restarting relay must not retry in lockstep.
//
// AND GATED ON THE SOCKET, which is the part that matters for the relay. A mint is
// POST /api/assets/media-token behind requireAuth, and requireAuth is FOUR sequential
// pool acquisitions. Retrying blindly through a ~30s restart is ~5.6 attempts per
// client — about 450 queries with 20 clients, 900 with 40, all landing on a relay
// that is still booting and on the exact pool that starves first. That would trade
// one bug for a worse one.
//
// So: while the socket is down the relay is unreachable and an HTTP mint would fail
// anyway, so we do not try. runReconnectBurst already calls warmMediaPass() the
// moment the socket is back, and its reconnect carries its own full jitter — that
// path owns the outage case and always did.
//
// This retry therefore covers only what reconnect does NOT: the socket is fine and
// the mint failed on its own (a transient 5xx, a pool blip, a race against the
// pass's own expiry). That is a handful of requests from one client, not a herd.
const RETRY_BASE_MS = 1_000;
const RETRY_MAX_MS = 30_000;
let retryAttempt = 0;
let retryTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Is this failure worth retrying? A relay that is restarting answers with a network
 * error or a 5xx and WILL come back. A 401/403 means the pass itself is refused —
 * a revoked session, a suspended account — and no amount of retrying mints it; that
 * would just be two pointless requests a minute, forever, from every open tab. 429
 * is the one 4xx that IS transient, so it keeps its retry.
 */
function isRetryable(err: unknown): boolean {
  const status = statusOf(err);
  if (typeof status !== "number") return true; // network/abort — no response at all
  if (status === 429) return true;
  return status < 400 || status >= 500;
}

// A failed mint's kind, from the facts the retry reads (no response: a TransportError, transient).
export function mintFailureClass(err: unknown): { failureKind?: FailureKind; code?: string } {
  if (err instanceof MediaPassAuthError) {
    return { failureKind: err.kind === "refused" ? "refusal" : "transient", code: err.kind };
  }
  const status = statusOf(err);
  if (typeof status !== "number") return {};
  const code = `http_${status}`;
  if (isRetryable(err)) return { failureKind: "transient", code };
  // 403 is the relay refusing this person; any other 4xx from our own route is our contract.
  return { failureKind: status === 403 ? "refusal" : "fault", code };
}

/**
 * The delay before mint attempt `attempt` (0-based), in milliseconds. FULL JITTER
 * over a doubling, capped envelope — the same shape the WS client's reconnect uses,
 * so N tabs that failed against the same restarting relay do not retry in lockstep.
 *
 * Exported because not every consumer can watch `token` change. A Svelte `$derived`
 * src recomputes by itself when the pass lands; a plain-DOM consumer (the PageImage
 * NodeView) never hears about it and has to re-ask on a schedule of its own. That
 * schedule must be THIS one — a second, hand-rolled policy is how one client ends up
 * hammering a relay the other is politely backing off from.
 */
export function mediaPassRetryDelayMs(attempt: number): number {
  const envelope = Math.min(RETRY_BASE_MS * 2 ** Math.min(attempt, 5), RETRY_MAX_MS);
  return Math.round(Math.random() * envelope);
}

function scheduleRetry(): void {
  if (retryTimer) return; // one pending retry at a time
  // Disconnected: reconnect owns the recovery. Reset so the first attempt after the
  // socket returns starts at the base delay instead of an escalated one.
  if (!client.connected) {
    cancelRetry();
    return;
  }
  const delay = mediaPassRetryDelayMs(retryAttempt);
  retryAttempt += 1;
  retryTimer = setTimeout(() => {
    retryTimer = null;
    void ensureMediaToken();
  }, delay);
  // Never hold a test runner or a closing tab open on a pending retry.
  (retryTimer as unknown as { unref?: () => void }).unref?.();
}

function cancelRetry(): void {
  if (retryTimer) clearTimeout(retryTimer);
  retryTimer = null;
  retryAttempt = 0;
}

function statusOf(err: unknown): number | undefined {
  return (err as { status?: number } | null)?.status;
}

/** mintMediaToken's typed "no sign-in token, nothing sent" (NotSignedInError). */
function isNotSignedIn(err: unknown): boolean {
  return (err as { code?: string } | null)?.code === "not_signed_in";
}

// CYBORG-1187. Set while waiting for a sign-in; calling it drops the listener.
let stopSignInWait: (() => void) | null = null;
// The client's authTokenRevision whose token the relay refused after one refresh.
let refusedAtRevision: number | null = null;

function waitForSignIn(): void {
  if (stopSignInWait) return; // one listener, however many images asked
  coldStartTrace("media-pass-wait-signin");
  stopSignInWait = client.on("connection", ({ status }) => {
    if (status !== "connected" || !client.hasAuthToken) return;
    coldStartTrace("media-pass-signin-seen");
    stopSignInWait?.();
    stopSignInWait = null;
    void ensureMediaToken();
  });
}

/**
 * A 401 the mint could not get past. `refused`: the token at `revision` is dead
 * (refresh said "expired", or a second 401 after a good refresh). `refresh_failed`:
 * the refresh itself failed transiently, so the normal backoff tries again.
 */
class MediaPassAuthError extends Error {
  constructor(
    readonly kind: "refused" | "refresh_failed",
    readonly revision: number,
    cause: unknown,
  ) {
    super(
      kind === "refused"
        ? "media-token refused: HTTP 401 after auth refresh"
        : "media-token 401: auth refresh failed (transient)",
      { cause },
    );
    this.name = "MediaPassAuthError";
  }
}

// A token that keeps changing under the mint gets this many fresh tries, no more.
const MAX_REVISION_CHASES = 2;

/**
 * One mint; on a 401 with a token in hand, one silent refresh of the sign-in token
 * and one more mint. The revision is read before EACH mint, so a 401 that belongs
 * to a token already replaced (socket reauth, new sign-in) just mints again.
 */
async function mintWithOneAuthRefresh(): Promise<{ token: string; expiresAt: number }> {
  let refreshed = false;
  let chases = 0;
  for (;;) {
    const revision = client.authTokenRevision;
    try {
      return await client.mintMediaToken();
    } catch (err) {
      if (statusOf(err) !== 401) throw err;
      if (client.authTokenRevision !== revision && chases < MAX_REVISION_CHASES) {
        chases += 1;
        continue;
      }
      if (refreshed) throw new MediaPassAuthError("refused", revision, err);
      const outcome = await client.refreshAuthToken();
      if (outcome === "expired") throw new MediaPassAuthError("refused", revision, err);
      if (outcome !== "refreshed") throw new MediaPassAuthError("refresh_failed", revision, err);
      refreshed = true;
    }
  }
}

/** Past `exp`, not merely inside the refresh skew. Such a pass can only ever 401. */
function isHardExpired(): boolean {
  return !!token && Date.now() >= expiresAtMs;
}

// `$state` (not a plain module variable) because the consumers are `$derived`
// image URLs: when the first mint lands, the avatars that painted without a pass
// have to recompute their src.
let token = $state<string | null>(null);
// Epoch ms. Plain — nothing re-renders off the expiry itself.
let expiresAtMs = 0;
// THE dedup: every concurrent caller awaits this same promise.
let inFlight: Promise<string | null> | null = null;

function isFresh(): boolean {
  return !!token && Date.now() < expiresAtMs - REFRESH_SKEW_MS;
}

/**
 * Resolve a usable media pass, minting one if the cached pass is missing or
 * close to expiry. N concurrent callers produce exactly ONE network request.
 * Resolves to null (never rejects) when the mint fails — the caller then builds
 * an unauthenticated URL, which is what the relay still serves today.
 */
export function ensureMediaToken(): Promise<string | null> {
  if (isFresh()) return Promise.resolve(token);
  if (inFlight) return inFlight;
  // Not signed in: the connect listener re-asks. Asking now could only 401.
  if (stopSignInWait) return Promise.resolve(null);
  // This sign-in token was already refused after one refresh. Wait for a new one.
  if (refusedAtRevision !== null) {
    if (refusedAtRevision === client.authTokenRevision) return Promise.resolve(null);
    refusedAtRevision = null;
  }
  inFlight = mintWithOneAuthRefresh()
    .then((mint) => {
      token = mint.token;
      // The relay reports `expiresAt` in epoch SECONDS (the JWT `exp` verbatim).
      expiresAtMs = mint.expiresAt * 1000;
      coldStartTrace("media-pass", { ok: true });
      cancelRetry();
      return token;
    })
    .catch((err) => {
      coldStartTrace("media-pass", {
        ok: false,
        status: statusOf(err),
        auth: err instanceof MediaPassAuthError ? err.kind : undefined,
        notSignedIn: isNotSignedIn(err),
        err: errorSlug(err),
      });
      if (isNotSignedIn(err)) {
        // Expected before the first connect: no request went out, so
        // nothing to report. Mint once the client is connected with a token.
        cancelRetry();
        waitForSignIn();
        return null;
      }
      // NOT a swallow, and not annotated as one. The CALLER degrades on purpose (it
      // builds the URL without `?t=`), but the failure itself is precisely the thing
      // nobody would ever see: with the flag ON — production, today — a broken mint
      // blanks EVERY image in the product at once and the only symptom is grey boxes. Beaconed to /api/cyborg/client-log →
      // logError("ui.client") → telemetry, the same shape app.svelte.ts uses for its
      // own degraded-but-not-silent failures.
      reportUiFailure("media.ensureMediaToken", err, {}, mintFailureClass(err));
      // Drop a pass that is already past `exp`. Two reasons, and the second is the
      // one that actually recovers the page: (a) it can only 401, so attaching it
      // buys nothing; (b) `token` is `$state`, so clearing it invalidates every
      // `$derived` image src and forces the repaint that a stuck tab never got.
      // A pass merely inside the refresh skew is still VALID and is deliberately
      // kept — dropping it would blank good images over a transient failure.
      if (isHardExpired()) {
        token = null;
        expiresAtMs = 0;
      }
      // Refused for good: reported once above, then quiet until the sign-in token
      // changes (CYBORG-1187; this used to re-ask on every render). A transient
      // refresh failure takes the normal backoff instead.
      if (err instanceof MediaPassAuthError) {
        if (err.kind === "refused") {
          refusedAtRevision = err.revision;
          cancelRetry();
        } else {
          scheduleRetry();
        }
        return null;
      }
      if (isRetryable(err)) scheduleRetry();
      else cancelRetry();
      return null;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

/**
 * The pass as it stands RIGHT NOW, for a synchronous `$derived` URL. Kicks off a
 * refresh in the background when there isn't a fresh one, so the first paint goes
 * out unauthenticated and the next one carries the pass.
 */
export function mediaToken(): string | null {
  if (!isFresh()) void ensureMediaToken();
  return token;
}

/**
 * Append the current pass to a relay asset URL. No pass ⇒ the URL unchanged.
 *
 * Guarded to `/api/assets/` (CYBORG-897 phase 4). The pass is a CREDENTIAL: it
 * belongs on our own relay's asset routes and nowhere else. Once attachment urls
 * became the permanent form this function started being called on whatever a
 * message carries — which includes `data:` uris, the bundled preset avatar art and,
 * on any legacy row, a raw S3 url. Handing a bearer-ish token to a third-party
 * origin because someone piped an arbitrary url through here is exactly the kind of
 * leak that is invisible until it is in someone's access log. Every pre-existing
 * caller passes a `/api/assets/...` url, so nothing moves.
 */
// The relay refused `pass` (401) though this clock calls it fresh: the next ensure mints. It stays
// attached until then; clearing it would strip the pass from every image on screen at once.
export function expireRefusedMediaPass(pass: string): void {
  if (token === pass) expiresAtMs = 0;
}

export function withMediaToken(url: string): string {
  if (!url.includes("/api/assets/")) return url;
  const t = mediaToken();
  if (!t) return url;
  return `${url}${url.includes("?") ? "&" : "?"}t=${encodeURIComponent(t)}`;
}

/**
 * THE render-time media resolver (CYBORG-897 phase 4). Stored url in, loadable
 * `src` out:
 *
 *   1. rewrite one of OUR stored asset urls (bucket / CloudFront) to the permanent
 *      `/api/assets/o/<key>` form — resolve at render, never migrate stored data;
 *   2. append the media pass, which is the only credential an `<img src>` can carry.
 *
 * Anything that is not ours (a `data:` uri, preset art, a self-host fs url) comes
 * back untouched. `client.httpBase` is the relay this client is ACTUALLY connected
 * to, which is why the rewrite lives here and not in the markdown renderer.
 */
export function mediaSrc(url: string | null | undefined): string {
  if (!url) return "";
  const base = client.httpBase || CLOUD_RELAY_HTTP_BASE;
  return withMediaToken(toAssetObjectUrl(url, base));
}

/** Loadable url (with the media pass) for a stored object key on the relay this client uses. */
export function mediaKeySrc(key: string): string {
  return mediaSrc(assetObjectUrl(client.httpBase || CLOUD_RELAY_HTTP_BASE, key));
}

/**
 * THE render-time AVATAR resolver for a raw `<img src>` (CYBORG-967). Stored
 * avatar value in (a bucket url, the permanent `/api/assets/o/<key>` form, preset
 * `/agent-avatars` art, a `data:`/`blob:` preview), loadable `src` out.
 *
 * Avatar.svelte already does this for every surface that mounts it; this is for
 * the raw `<img>` paints that never did (sidebar rows, tables, pickers, the
 * create/edit previews) and painted the stored url as-is — which is a broken
 * picture the moment `avatars/` leaves the bucket's public list. Same two legs
 * as Avatar's, in the same order:
 *
 *   - `renderWidth` given ⇒ the relay thumbnailer (a right-sized WebP), which is
 *     what every Avatar mount asks for first;
 *   - otherwise, or for a url the thumbnailer does not address ⇒ avatarImageUrl,
 *     the permanent object url with the pass on it.
 *
 * Unlike Avatar there is no onerror leg: a raw `<img>` has no glyph to fall back
 * to, and adding one at thirty sites is a different change than routing the url.
 *
 * NOT ours (preset art, a `data:`/`blob:` preview, a foreign host) ⇒ returned
 * verbatim and, deliberately, WITHOUT touching `mediaToken()`: a preview that
 * never needs the pass must not be the thing that kicks a mint, and (CYBORG-963)
 * a user-settable avatar on someone else's host must never be handed the
 * credential. avatarImageUrl is origin-guarded for that case too; the early
 * return here just keeps the mint out of it. "Ours" = a url that carries one of
 * our storage keys, or any `/api/assets/` url on the relay we are connected to
 * (the self-host fs form, which has no key but does take the pass).
 */
export function avatarSrc(url: string | null | undefined, renderWidth?: number): string {
  if (!url) return "";
  const base = (client.httpBase || CLOUD_RELAY_HTTP_BASE).replace(/\/+$/, "");
  // A bucket / CloudFront url is ours wherever it points; the permanent object FORM
  // is host-agnostic (assetObjectKey reads only the shape), so it counts only on
  // the relay we are connected to — the same origin bound avatarImageUrl applies.
  const ours =
    url.startsWith(`${base}/api/assets/`) || (!!assetKeyFromUrl(url) && !assetObjectKey(url));
  if (!ours) return (renderWidth ? presetAvatarThumbUrl(url, renderWidth) : null) ?? url;
  const pass = mediaToken();
  if (renderWidth) {
    const thumb = avatarThumbUrl(url, renderWidth, base, pass);
    if (thumb) return thumb;
  }
  return avatarImageUrl(url, base, pass);
}

/**
 * Build a thumbnail URL for a storage key. `relayBase` MUST be absolute — on
 * desktop the renderer origin is `cyborg://app`, whose protocol handler serves
 * local static files only, so a relative /api/... returns index.html.
 */
export function assetUrl(key: string, opts: { width: number; relayBase?: string }): string {
  const base = (opts.relayBase || client.httpBase || CLOUD_RELAY_HTTP_BASE).replace(/\/+$/, "");
  return withMediaToken(`${base}/api/assets/thumb?key=${encodeURIComponent(key)}&w=${opts.width}`);
}

/** Test seam — the cache is module-global. */
export function __resetMediaToken(): void {
  token = null;
  expiresAtMs = 0;
  inFlight = null;
  cancelRetry();
  stopSignInWait?.();
  stopSignInWait = null;
  refusedAtRevision = null;
}
